import axios from 'axios';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { MarktingContext } from '../../Context/MarktingContext';
import { useNavigate } from 'react-router-dom';

export default function HTMLViewer({
  id,
  comments,
  setActiveComment,
  loginToken,
  archived,
  setDocumentStatus,
  activeCommentFromFeed // New prop for eye icon functionality
}) {
  const { setMarktingData, marktingData, setArchivedData, archivedData } = useContext(MarktingContext);

  const [content, setContent] = useState('');
  const [activeElementId, setActiveElementId] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [formPosition, setFormPosition] = useState({ top: 0, left: 0 });
  const [highlightedElementId, setHighlightedElementId] = useState(null); // New state for highlighting
  const commentElementRefs = useRef({}); // New ref to store comment element references
  const navigation = useNavigate();

  // New effect to handle activeCommentFromFeed changes
  useEffect(() => {
    if (activeCommentFromFeed && activeCommentFromFeed.elementId) {
      const commentElement = document.getElementById(activeCommentFromFeed.elementId);
      
      if (commentElement) {
        // Scroll the comment into view
        commentElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        
        // Highlight the comment
        setHighlightedElementId(activeCommentFromFeed.elementId);
        
        // Apply special highlight styling
        commentElement.style.outline = '3px solid var(--accent)';
        commentElement.style.backgroundColor = 'rgba(195, 29, 138, 0.1)';
        commentElement.style.boxShadow = '0 0 15px rgba(195, 29, 138, 0.5)';
        commentElement.style.transition = 'all 0.3s ease';
        commentElement.style.transform = 'scale(1.02)';
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedElementId(null);
          commentElement.style.outline = '2px solid var(--accent)'; // Return to normal comment outline
          commentElement.style.backgroundColor = '';
          commentElement.style.boxShadow = '';
          commentElement.style.transform = '';
        }, 3000);
      }
    }
  }, [activeCommentFromFeed]);

  useEffect(() => {
    // AbortController to cancel the fetch if the component is unmounted.
    // Unmounted is when the component is removed from the DOM
    const controller = new AbortController();
    const fetchContent = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/content/${id}`,
          {
            headers: {
              Authorization: `Bearer ${loginToken}`,
            }
          }
        );
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = res?.data?.fileContent;
        // assignIds(tempDiv);
        setContent(tempDiv.innerHTML);
      } catch (error) {
        if (error?.response?.status === 401) {
          localStorage.removeItem("Firebase Auth");
          localStorage.removeItem("okta-token-storage");
          localStorage.removeItem("okta-cache-storage");
          navigation('/');
          // console.error("session expired");
          return;
        };
      }
    };

    fetchContent();
    return () => controller.abort(); // Cleanup function
  }, [id, loginToken, navigation]);


  useEffect(() => {
    if (!content) return;

    let comments = [];
    // Use the appropriate data source based on archived status
    const dataSource = archived ? archivedData : marktingData;

    const element = dataSource?.find((item) =>
      item?.documentId === id || item?.version?.some(version => version.versionId === id)
    );

    if (element) {
      comments = element.documentId === id
        ? element?.Comments ?? []
        : element?.version?.find(version => version.versionId === id)?.Comments ?? [];
    }

    // Clear previous comment element references
    commentElementRefs.current = {};

    // Batch update DOM
    const elementsToUpdate = [];
    comments?.forEach((comment) => {
      const element = document.getElementById(comment.elementId);
      if (element) {
        elementsToUpdate.push(element);
        // Store reference to this comment element
        commentElementRefs.current[comment.elementId] = element;
      }
    });

    elementsToUpdate.forEach(element => {
      element.style.outline = '2px solid var(--accent)';
    });

  }, [content, archived ? archivedData : marktingData, id, archived]);

  // Calculate optimal position for the comment form
  const calculateFormPosition = (clickEvent, clickedElement) => {
    const formWidth = 320;
    const formHeight = 200; // Approximate height of the form
    const padding = 10;

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Get the container element (overflow-auto div)
    const container = clickEvent.currentTarget.closest('.overflow-auto');
    const containerRect = container ? container.getBoundingClientRect() : { top: 0, left: 0, right: viewportWidth, bottom: viewportHeight };

    // Get click position relative to viewport
    let x = clickEvent.clientX;
    let y = clickEvent.clientY;

    // Calculate preferred position (to the right and below the click)
    let left = x + padding;
    let top = y + padding;

    // Adjust horizontal position if form would go outside viewport or container
    if (left + formWidth > Math.min(viewportWidth, containerRect.right) - padding) {
      left = x - formWidth - padding; // Position to the left of click
    }
    if (left < Math.max(0, containerRect.left) + padding) {
      left = Math.max(0, containerRect.left) + padding; // Align to left edge with padding
    }

    // Adjust vertical position if form would go outside viewport or container
    if (top + formHeight > Math.min(viewportHeight, containerRect.bottom) - padding) {
      top = Math.max(y - formHeight - padding, Math.max(0, containerRect.top) + padding); // Position above click or at top
    }
    if (top < Math.max(0, containerRect.top) + padding) {
      top = Math.max(0, containerRect.top) + padding; // Align to top edge with padding
    }

    // Ensure the form stays within the container bounds
    left = Math.max(containerRect.left + padding, Math.min(left, containerRect.right - formWidth - padding));
    top = Math.max(containerRect.top + padding, Math.min(top, containerRect.bottom - formHeight - padding));

    return { top, left };
  };

  // When an element is clicked, highlight it and show the comment form.
  const handleClick = (e) => {
    // Don't allow adding new comments if archived
    if (archived) return;

    const clickedElement = e.target;

    if (!clickedElement.id) return; // Ignore clicks on elements without an ID

    setActiveComment({ elementId: clickedElement?.id });

    // Calculate and set form position
    const position = calculateFormPosition(e, clickedElement);
    setFormPosition(position);

    // Remove highlight from the previously active element if it isn't saved.
    if (activeElementId && activeElementId !== clickedElement.id) {
      // Here, we check in the comments array if a comment exists for the previous element.
      const prevSaved = comments?.find(
        (comment) => comment.elementId === activeElementId
      );
      if (!prevSaved) {
        const prevElement = document.getElementById(activeElementId);
        if (prevElement) prevElement.style.outline = '';
      }
    }

    // Highlight the clicked element using an outline.
    clickedElement.style.outline = '2px solid var(--accent)';

    const commentedBefore = comments.some((comment) => comment.elementId === clickedElement?.id);
    if (!commentedBefore) {
      setActiveElementId(clickedElement.id);
    }else{
      setActiveElementId(null)
    }
    setCommentText(''); // Clear any previous comment text.
  };

  // Save the comment to local storage and leave the highlight.
  const handleSave = async () => {
    if (!activeElementId) return;

    // Don't allow saving comments if archived
    if (archived) {
      console.log("Cannot add comments to archived content");
      return;
    }

    const element = document.getElementById(activeElementId);
    if (!element) return;

    const payload = {
      elementId: activeElementId,
      comment: commentText.trim(),
    };

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/comment`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
          params: { id }, // Placed inside the same config object
        }
      );

      if (data?.data) {
        const deepClone = JSON.parse(JSON.stringify(marktingData));

        // Find the element with matching documentId or versionId
        const element = deepClone.find((item) =>
          item.documentId === id ||
          item.version?.some(version => version.versionId === id)
        );
        if (!element) {
          // console.error('Element not found');
          return; // If no matching element, exit the function
        }

        if (element.documentId === id) {
          if (!element.Comments) {
            element.Comments = [data.data];
          } else {
            element.Comments.push(data.data);
          }
          element.status = data.status;
        } else {
          const version = element.version.find(v => v.versionId === id);
          version.Comments = version.Comments || [];
          version.Comments.push(data.data);
          version.status = data.status;
        }
        setDocumentStatus(data.status);
        setMarktingData(deepClone);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("Firebase Auth");
        localStorage.removeItem("okta-token-storage");
        localStorage.removeItem("okta-cache-storage");
        navigation('/');
        // console.error("session expired");
        return;
      };
      // console.error("Error saving comment:", error);
    }

    setActiveElementId(null);
    setCommentText('');
  };

  // Cancel the comment: remove the temporary highlight (if not saved) and hide the form.
  const handleCancel = () => {
    if (!activeElementId) return;
    // Check if there's already a saved comment for the active element.
    const savedCommentForElement = comments?.find(
      (comment) => comment.elementId === activeElementId
    );
    // If no saved comment exists, remove the outline.
    if (!savedCommentForElement) {
      const element = document.getElementById(activeElementId);
      if (element) {
        element.style.outline = '';
      }
    }
    setActiveElementId(null);
    setCommentText('');
  };

  return (
    <div style={{ position: 'relative' }}>
      <div onClick={handleClick}>
        <div
          className="overflow-auto vh-100"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
      {activeElementId && !archived && (
        <div
          className="form-control"
          style={{
            position: 'fixed',
            top: `${formPosition.top}px`,
            left: `${formPosition.left}px`,
            padding: '10px',
            border: '1px solid #ccc',
            backgroundColor: 'white',
            zIndex: 1000,
            width: '320px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            borderRadius: '8px',
          }}
        >
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Enter your comment here..."
            style={{
              width: '100%',
              height: '130px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '8px',
              resize: 'none'
            }}
            autoFocus
          />
          <div className="d-flex gap-2 mt-2">
            <button
              className="b-pink-color flex-grow-1"
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
            >
              Save
            </button>
            <button
              className="btn btn-outline-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}